"use client";

import {
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import type { DOSAGE_CALCULATION_CALCULATOR_STRINGS } from "@/components/tools/dosage-calculation-calculator";
import type {
  DosageFields,
  SupplyForm,
} from "@/components/tools/stats-clinical/dosage-compute";
import { tPlural } from "@/lib/i18n/interpolate";
import { UNITS } from "@/lib/tool-kit/stats-clinical/dosage";

/*
 * The input groups of the dosage-calculation practice calculator, one per mode. Strings are
 * the widget's own; unit symbols (mg, mL, kg) are not words and live with the lib.
 */

type Strings = typeof DOSAGE_CALCULATION_CALCULATOR_STRINGS;
type Setter = <K extends keyof DosageFields>(
  key: K
) => (value: DosageFields[K]) => void;

interface GroupProps {
  fields: DosageFields;
  set: Setter;
  strings: Strings;
}

const DOSE_UNITS = ["mg", "g", "mcg", "units", "mEq"] as const;
const FORMS: readonly SupplyForm[] = ["tablet", "capsule", "ml"];
const MASS_UNITS = UNITS.filter(
  (unit) =>
    unit.category === "mass" && unit.symbol !== "lb" && unit.symbol !== "kg"
).map((unit) => ({ value: unit.symbol, label: unit.symbol }));
const ALL_UNITS = UNITS.map((unit) => ({
  value: unit.symbol,
  label: unit.symbol,
}));

function doseOptions(strings: Strings) {
  const names: Record<string, string> = {
    units: strings.unitUnits,
    mEq: strings.unitMeq,
  };
  return DOSE_UNITS.map((value) => ({ value, label: names[value] ?? value }));
}

export function DesiredHaveFields({
  fields,
  set,
  strings,
  locale,
}: GroupProps & { locale: string }) {
  const options = doseOptions(strings);
  const formName = (form: SupplyForm) => {
    if (form === "ml") {
      return "mL";
    }
    const template =
      form === "tablet" ? strings.formTablet : strings.formCapsule;
    return tPlural(template, locale, 2, { count: "" }).trim();
  };
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <TextField
        id="dosage-desired"
        label={strings.desiredLabel}
        numeric
        onChange={set("desired")}
        value={fields.desired}
      />
      <SelectField
        id="dosage-desired-unit"
        label={strings.unitLabel}
        onChange={set("desiredUnit")}
        options={options}
        value={fields.desiredUnit}
      />
      <div />
      <TextField
        id="dosage-have"
        label={strings.haveLabel}
        numeric
        onChange={set("have")}
        value={fields.have}
      />
      <SelectField
        id="dosage-have-unit"
        label={strings.unitLabel}
        onChange={set("haveUnit")}
        options={options}
        value={fields.haveUnit}
      />
      <div />
      <TextField
        hint={strings.quantityHint}
        id="dosage-quantity"
        label={strings.quantityLabel}
        numeric
        onChange={set("quantity")}
        value={fields.quantity}
      />
      <SelectField
        id="dosage-form"
        label={strings.formLabel}
        onChange={set("form")}
        options={FORMS.map((value) => ({ value, label: formName(value) }))}
        value={fields.form}
      />
    </div>
  );
}

export function WeightFields({ fields, set, strings }: GroupProps) {
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.basisLabel}
        name="dosage-basis"
        onChange={set("basis")}
        options={[
          { value: "dose", label: strings.basisDose },
          { value: "day", label: strings.basisDay },
        ]}
        value={fields.basis}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="dosage-perkg"
          label={strings.orderLabel}
          numeric
          onChange={set("perKg")}
          value={fields.perKg}
        />
        <SelectField
          id="dosage-dose-unit"
          label={strings.orderUnitLabel}
          onChange={set("doseUnit")}
          options={MASS_UNITS}
          value={fields.doseUnit}
        />
        {fields.basis === "day" ? (
          <TextField
            id="dosage-doses"
            label={strings.dosesLabel}
            numeric
            onChange={set("doses")}
            value={fields.doses}
          />
        ) : (
          <div />
        )}
        <TextField
          id="dosage-weight"
          label={strings.weightLabel}
          numeric
          onChange={set("weight")}
          value={fields.weight}
        />
        <SelectField
          id="dosage-weight-unit"
          label={strings.weightUnitLabel}
          onChange={set("weightUnit")}
          options={[
            { value: "kg", label: "kg" },
            { value: "lb", label: "lb" },
          ]}
          value={fields.weightUnit}
        />
      </div>
      <p className="font-medium text-foreground text-sm">
        {strings.supplyHeading}
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="dosage-supply-have"
          label={strings.supplyHave}
          numeric
          onChange={set("supplyHave")}
          value={fields.supplyHave}
        />
        <SelectField
          id="dosage-supply-unit"
          label={strings.supplyUnit}
          onChange={set("supplyUnit")}
          options={MASS_UNITS}
          value={fields.supplyUnit}
        />
        <TextField
          id="dosage-supply-quantity"
          label={strings.supplyQuantity}
          numeric
          onChange={set("supplyQuantity")}
          value={fields.supplyQuantity}
        />
      </div>
    </div>
  );
}

export function ConvertFields({ fields, set, strings }: GroupProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <TextField
        id="dosage-convert-value"
        label={strings.convertValue}
        numeric
        onChange={set("convertValue")}
        value={fields.convertValue}
      />
      <SelectField
        id="dosage-convert-from"
        label={strings.convertFrom}
        onChange={set("convertFrom")}
        options={ALL_UNITS}
        value={fields.convertFrom}
      />
      <SelectField
        id="dosage-convert-to"
        label={strings.convertTo}
        onChange={set("convertTo")}
        options={ALL_UNITS}
        value={fields.convertTo}
      />
    </div>
  );
}
